/* eslint-disable tsdoc/syntax */
import { StrictMode } from "react";
import * as ReactDOM from "react-dom/client";
import { Provider } from "react-redux";
import { initSentry, ErrorBoundary } from "@zenith/utils/sentry";
// import "./registerServiceWorker";

import { StyledEngineProvider } from "@mui/material/styles";

import App from "@zenith/app/app";
import { store } from "@zenith/app/store";
import "./utils/ExtensionMethods";
import { OpenAPI as UserOpenAPI } from "@zenith/services/user";
import { OpenAPI as TransactionOpenAPI } from "@zenith/services/transactions";
import { OpenAPI as InventoryOpenAPI } from "@zenith/services/inventory";
import { installApiErrorReporting, installConnectionWatch } from "./services";
import { startPaygilant } from "./utils/paygilant";

// Set absolute API base URLs from env vars so calls go to api.finopay.com
// instead of resolving relative to the current domain (app.finopay.com)
UserOpenAPI.BASE =
  import.meta.env.VITE_SWAGGER_USER_SERVICE ?? UserOpenAPI.BASE;
TransactionOpenAPI.BASE =
  import.meta.env.VITE_SWAGGER_TRANSACTIONS_SERVICE ?? TransactionOpenAPI.BASE;
InventoryOpenAPI.BASE =
  import.meta.env.VITE_SWAGGER_INVENTORY_SERVICE ?? InventoryOpenAPI.BASE;

const envType = import.meta.env.VITE_USER_DEPLOYMENT_INSTANCE_TYPE;
// Installed before anything renders, so no request can fail unreported.
installApiErrorReporting();
installConnectionWatch();
initSentry();
startPaygilant();

// ─── Chunk error recovery ────────────────────────────────────────────────────
// Both handlers use the same sessionStorage key ('autoReloadAttempted') so
// they coordinate with ChunkErrorBoundary and the app.tsx handlers.
// One reload attempt max — if it still fails the user sees the error page.

function recoverFromChunkError() {
  if (sessionStorage.getItem("autoReloadAttempted") === "true") return;
  sessionStorage.setItem("autoReloadAttempted", "true");
  if ("caches" in window) {
    caches
      .keys()
      .then((keys) =>
        Promise.all(keys.map((k) => caches.delete(k))).finally(() =>
          globalThis.location.reload()
        )
      );
  } else {
    globalThis.location.reload();
  }
}

// Catches dynamic import() rejections (ChunkLoadError / Failed to fetch module)
window.addEventListener(
  "unhandledrejection",
  (event: PromiseRejectionEvent) => {
    const err = event.reason;
    const isChunkError =
      err?.name === "ChunUtilityApiService.getAkLoadError" ||
      err?.message?.includes("Failed to fetch dynamically imported module") ||
      err?.message?.includes("Importing a module script failed");
    if (isChunkError) recoverFromChunkError();
  }
);

// Catches synchronous script load failures (capture phase, fires before React)
window.addEventListener(
  "error",
  (event: ErrorEvent) => {
    const target = event.target as HTMLElement | null;
    const isMissingChunk =
      target?.nodeName === "SCRIPT" &&
      (target as HTMLScriptElement).src?.includes("/assets/");
    if (isMissingChunk) recoverFromChunkError();
  },
  true
);

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

const ErrorFallback = ({
  error,
  resetError,
}: {
  error: Error;
  resetError: () => void;
}) => {
  return (
    <div className="error-fallback">
      <h2>Something went wrong</h2>
      <details>
        <summary>Error Details</summary>
        <pre>{error.message}</pre>
      </details>
      <button onClick={resetError}>Try again</button>
    </div>
  );
};

// Use FallbackRender type properly
const fallbackRender: React.ComponentProps<
  typeof ErrorBoundary
>["fallback"] = ({ error, resetError }) => (
  <ErrorFallback error={error as Error} resetError={resetError} />
);

const AppWrapper = () => (
  <Provider store={store}>
    <StyledEngineProvider injectFirst>
      <App />
    </StyledEngineProvider>
  </Provider>
);

// Temporary fix while we don't have the production builds ready
if (envType === "SIT" || envType === "UAT" || envType === "PRODUCTION") {
  root.render(
    <ErrorBoundary fallback={fallbackRender} showDialog>
      <AppWrapper />
    </ErrorBoundary>
  );
} else {
  root.render(
    <ErrorBoundary fallback={fallbackRender} showDialog>
      <StrictMode>
        <AppWrapper />
      </StrictMode>
    </ErrorBoundary>
  );
}
